import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { dig } from '@imcc/utilities';
import { DefaultOAuthInterceptor, OAuthModuleConfig } from 'angular-oauth2-oidc';
import { EMPTY, Observable } from 'rxjs';
import {
  mergeMap, catchError, filter, first, timeout,
} from 'rxjs/operators';
import { AuthService } from './auth.service';

const TIMEOUT_MS = 3000;

/*
 * Our authentication library does not act on expired tokens. When users left
 * the app open with the computer in sleep mode, the app would get stuck in an
 * unauthenticated state.
 *
 * This interceptor first tries to retrieve a new access token. If successful
 * it will attach that token to the next request. If unsuccessful it will sign
 * out.
 */
@Injectable()
export class StaleAuthInterceptor implements HttpInterceptor {
  constructor(
    private oAuthInterceptor: DefaultOAuthInterceptor,
    private authService: AuthService,
    private moduleConfig: OAuthModuleConfig,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.skipUrl(req.url.toLowerCase())) {
      return next.handle(req);
    }

    return this.requireAuthentication$.pipe(
      mergeMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.attachTokenToRequest(req, next);
        }

        this.authService.logout();
        return EMPTY;
      }),
    );
  }

  private attachTokenToRequest(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.oAuthInterceptor.intercept(req, next);
  }

  private requireAuthentication$: Observable<boolean> = this.authService.isAuthenticated$.pipe(
    filter(Boolean),
    first(),
    timeout(TIMEOUT_MS),
    catchError(async () => {
      await this.authService.refreshToken();
      return this.authService.isAuthenticated();
    }),
  ) as Observable<boolean>;

  skipUrl(url: string): boolean {
    const allowedUrls = dig(this.moduleConfig, 'resourceServer', 'allowedUrls') || [];

    return !allowedUrls.find((prefix: string) => url.toLowerCase().startsWith(prefix));
  }
}
